"use client";

import { useState } from "react";
import {
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  AngleOutcome,
  MissingOutcome,
  SidesOutcome,
} from "@/components/tools/geometry/polygon-angle-views";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import type { AngleInputUnit } from "@/lib/tool-kit/geometry/angle-input";
import type { AngleKind } from "@/lib/tool-kit/geometry/polygon-angles";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";

type Mode = "sides" | "interior" | "exterior" | "missing";

/** English strings. A translated content file overrides these through `ui`. */
export const POLYGON_ANGLE_CALCULATOR_STRINGS = {
  modeLabel: "What do you know?",
  modes: {
    sides: "The number of sides",
    interior: "One interior angle of a regular polygon",
    exterior: "One exterior angle of a regular polygon",
    missing: "All but one angle of any polygon",
  },
  sidesLabel: "Number of sides (n)",
  interiorLabel: "Interior angle",
  exteriorLabel: "Exterior angle",
  angleUnitLabel: "Angle unit",
  angleUnits: { deg: "Degrees", rad: "Radians" },
  angleHint:
    "In radians, type a multiple of π such as 3pi/5 to keep the answer exact.",
  knownLabel: "The known angles, in degrees",
  knownHint: "Separate the angles with spaces, semicolons or new lines.",
  results: "Results",
  names: {
    3: "triangle",
    4: "quadrilateral",
    5: "pentagon",
    6: "hexagon",
    7: "heptagon",
    8: "octagon",
    9: "nonagon",
    10: "decagon",
    11: "hendecagon",
    12: "dodecagon",
    13: "tridecagon",
    14: "tetradecagon",
    15: "pentadecagon",
    16: "hexadecagon",
    17: "heptadecagon",
    18: "octadecagon",
    19: "enneadecagon",
    20: "icosagon",
  },
  genericName: "{n}-gon",
  polygonIs: "A regular {name} has {n} sides.",
  sidesTotal: "Sides",
  diagonals: "Diagonals",
  measurementHeader: "Angle",
  degreesHeader: "Degrees",
  radiansHeader: "Radians",
  rows: {
    interiorSum: "Sum of the interior angles",
    interiorEach: "Each interior angle (regular)",
    exteriorEach: "Each exterior angle (regular)",
    exteriorSum: "Sum of the exterior angles",
    central: "Central angle (regular)",
  },
  workingHeading: "The working",
  figureHeading: "Figure",
  figureTitle:
    "A regular {name} with an interior, an exterior and a central angle marked",
  figureKey:
    "α is an interior angle, β an exterior angle (between a side extended and the next side) and γ the central angle.",
  downloadFigure: "Download figure (PNG)",
  copyResult: "Copy results",
  copied: "Copied!",
  notWhole:
    "No regular polygon has that angle: it would need {n} sides, and a polygon has a whole number of sides.",
  missingResult: "Missing angle",
  missingTotal: "Sum of all {n} interior angles",
  missingKnown: "Sum of the known angles",
  reflex:
    "The missing angle is 180° or more, so the polygon is concave at that corner.",
  summarySides:
    "{name}: interior angle sum {sum}°, each interior angle {each}°, each exterior angle {ext}°",
  summaryMissing: "Missing angle: {missing}°",
  enter: "Enter a value to see the angles.",
  enterAngles: "Enter the known angles to find the missing one.",
  invalidSides: "Enter a whole number of sides from 3 to 1000.",
  invalidAngle:
    "Enter the angle as a number; in radians you can also write multiples of π, such as 3pi/5.",
  range:
    "Enter an interior angle between 0° and 180°, or an exterior angle between 0° and 120°.",
  invalidList: "Enter the known angles as numbers separated by spaces.",
  countError:
    "A polygon with {n} sides needs {need} known angles; you entered {have}.",
  rangeError: "Each angle must be greater than 0° and less than 360°.",
  totalError:
    "These angles already use up the whole sum: the missing angle would not be between 0° and 360°.",
} as const;

const ID_PREFIX = "pa";

const EXAMPLE_ANGLE = {
  interior: { deg: "135", rad: "3pi/4" },
  exterior: { deg: "45", rad: "pi/4" },
} as const;

export default function PolygonAngleCalculatorWidget() {
  const strings = useToolUi(POLYGON_ANGLE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const [mode, setMode] = useState<Mode>("sides");
  const [sidesText, setSidesText] = useState("5");
  const [angleText, setAngleText] = useState("135");
  const [angleUnit, setAngleUnit] = useState<AngleInputUnit>("deg");
  const [missingSides, setMissingSides] = useState("4");
  const [knownText, setKnownText] = useState("90 80 100");
  const angleKind: AngleKind = mode === "exterior" ? "exterior" : "interior";

  function changeMode(next: Mode) {
    setMode(next);
    setAngleText(
      EXAMPLE_ANGLE[next === "exterior" ? "exterior" : "interior"].deg
    );
    setAngleUnit("deg");
  }

  let body: React.ReactNode;
  if (mode === "sides") {
    body = (
      <SidesOutcome fmt={fmt} mark={mark} strings={strings} text={sidesText} />
    );
  } else if (mode === "missing") {
    body = (
      <MissingOutcome
        anglesText={knownText}
        fmt={fmt}
        mark={mark}
        sidesText={missingSides}
        strings={strings}
      />
    );
  } else {
    body = (
      <AngleOutcome
        fmt={fmt}
        kind={angleKind}
        mark={mark}
        strings={strings}
        text={angleText}
        unit={angleUnit}
      />
    );
  }

  return (
    <>
      <div className="space-y-4">
        <SelectField
          id={`${ID_PREFIX}-mode`}
          label={strings.modeLabel}
          onChange={changeMode}
          options={(["sides", "interior", "exterior", "missing"] as const).map(
            (id) => ({ value: id, label: strings.modes[id] })
          )}
          value={mode}
        />
        {mode === "sides" ? (
          <TextField
            id={`${ID_PREFIX}-sides`}
            label={strings.sidesLabel}
            numeric
            onChange={setSidesText}
            value={sidesText}
          />
        ) : null}
        {mode === "interior" || mode === "exterior" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={angleUnit === "rad" ? strings.angleHint : undefined}
              id={`${ID_PREFIX}-angle`}
              label={
                mode === "interior"
                  ? strings.interiorLabel
                  : strings.exteriorLabel
              }
              mono
              onChange={setAngleText}
              value={angleText}
            />
            <SelectField
              id={`${ID_PREFIX}-unit`}
              label={strings.angleUnitLabel}
              onChange={(next) => {
                setAngleUnit(next);
                setAngleText(EXAMPLE_ANGLE[angleKind][next]);
              }}
              options={(["deg", "rad"] as const).map((id) => ({
                value: id,
                label: strings.angleUnits[id],
              }))}
              value={angleUnit}
            />
          </div>
        ) : null}
        {mode === "missing" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`${ID_PREFIX}-missing-sides`}
              label={strings.sidesLabel}
              numeric
              onChange={setMissingSides}
              value={missingSides}
            />
            <TextAreaField
              hint={strings.knownHint}
              id={`${ID_PREFIX}-known`}
              label={strings.knownLabel}
              onChange={setKnownText}
              rows={3}
              value={knownText}
            />
          </div>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
